import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import { Menu, MenuItem, Tooltip } from "@libraries/mui/components";
import { LauncherMessages } from "@languages/interfaces";
import ModuleHex from "@atoms/ModuleHex";
import ModuleUpsellModal from "@organisms/ModuleUpsellModal";
import AnnouncementBanner from "@organisms/AnnouncementBanner";
import { useTenantProducts } from "@hooks/useTenantProducts";
import { MODULE_PRESENTATION } from "./catalog";
import { openProduct } from "./openProductStrategy";
import { Module, ModuleState } from "./types";
import * as Styled from "./styles";

const LICENSE_WARNING_DAYS = 7;

// Stable keys for the loading-skeleton card slots (two grid rows at the
// widest breakpoint). Placeholder cards, not data — see Styled.SkeletonCard.
const SKELETON_SLOTS = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"];

type LicenseStatus = "normal" | "warning" | "expired";

/** Derives the launcher license state from the tenant's expiration date. */
const getLicenseStatus = (
  licenseExpiresAt: string | null
): { status: LicenseStatus; daysRemaining: number } => {
  if (!licenseExpiresAt) return { status: "normal", daysRemaining: 0 };

  const expiry = new Date(licenseExpiresAt).getTime();

  if (Number.isNaN(expiry)) return { status: "normal", daysRemaining: 0 };

  const msRemaining = expiry - Date.now();

  if (msRemaining <= 0) return { status: "expired", daysRemaining: 0 };

  const daysRemaining = Math.ceil(msRemaining / 86400000);

  return {
    status: daysRemaining <= LICENSE_WARNING_DAYS ? "warning" : "normal",
    daysRemaining
  };
};

/**
 * 3-state rule for a card.
 * - `contracted=false` → locked-not-contracted (click opens upsell).
 * - `contracted=true && !accessibleByCurrentUser` → available-no-access
 *   (visible but disabled; tooltip directs the user to the admin).
 * - `contracted=true && accessibleByCurrentUser` → accessible.
 */
const deriveState = (contracted: boolean, accessibleByCurrentUser: boolean): ModuleState => {
  if (!contracted) return "locked-not-contracted";
  if (!accessibleByCurrentUser) return "available-no-access";

  return "accessible";
};

/**
 * Derives the grouped card's state from its children. The group is accessible
 * when any child is accessible; otherwise available (disabled) when any child
 * is contracted; otherwise locked (opens upsell). Mirrors {@link deriveState}
 * at the group level.
 */
const deriveGroupState = (children: Module[]): ModuleState => {
  if (children.some((child) => child.state === "accessible")) return "accessible";
  if (children.some((child) => child.state !== "locked-not-contracted")) return "available-no-access";

  return "locked-not-contracted";
};

/**
 * Collapses the FMX (id "fmx") and DRIVER (id "driver") cards into one grouped
 * card when BOTH are present, placing the group at FMX's original position and
 * dropping the individual entries. When only one of the two is present it is
 * left untouched (defensive — no group is formed for a single product).
 */
const groupFmxSuite = (modules: Module[]): Module[] => {
  const fmx = modules.find((mod) => mod.id === "fmx");
  const driver = modules.find((mod) => mod.id === "driver");

  if (!fmx || !driver) return modules;

  const children = [fmx, driver];

  const group: Module = {
    id: "fmx-suite",
    moduleId: "fmx-suite",
    label: "FMX",
    description: LauncherMessages.launcherModuleDescFmxSuite,
    accent: "#D52924",
    state: deriveGroupState(children),
    children
  };

  return modules
    .map((mod) => (mod.id === "fmx" ? group : mod))
    .filter((mod) => mod.id !== "driver");
};

const Launcher: React.FC = () => {
  const { t } = useTranslation();
  const { instance } = useMsal();

  const [upsellModule, setUpsellModule] = useState<Module | null>(null);

  // Anchor + payload for the grouped-card menu (FMX suite). `menuOpen` drives the
  // MUI transition; the anchor + payload are kept until the close animation ends
  // (see the Menu's onExited) so the items don't blank out mid-fade.
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuModule, setMenuModule] = useState<Module | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const { products, licenseExpiresAt, loading, error } = useTenantProducts();

  const userName = instance.getActiveAccount()?.name ?? "";
  const license = getLicenseStatus(licenseExpiresAt);

  // Merge backend products with the frontend presentation catalog. Products
  // with no presentation are skipped. Cards render in the fixed MODULE_PRESENTATION
  // key order — no state-based reordering, so the sequence is always the same
  // regardless of each card's contracted/access state.
  const modules = useMemo<Module[]>(
    () => Object.entries(MODULE_PRESENTATION)
      .map(([alias, presentation]): Module | null => {
        const product = products.find((item) => item.module === alias);

        if (!product) return null;

        return {
          ...presentation,
          moduleId: product.moduleId,
          state: deriveState(product.contracted, product.accessibleByCurrentUser),
          url: product.baseUrl ?? undefined
        };
      })
      .filter((mod): mod is Module => mod !== null),
    [products]
  );

  // Collapse FMX + DRIVER into a single grouped card (both are always sold
  // together). Runs after the raw catalog merge so the group inherits the
  // per-child state.
  const groupedModules = useMemo<Module[]>(() => groupFmxSuite(modules), [modules]);

  const activeCount = groupedModules.filter((mod) => mod.state === "accessible").length;

  const handleCardClick = (mod: Module) => {
    if (license.status === "expired") return;

    switch (mod.state) {
      case "locked-not-contracted":
        setUpsellModule(mod);

        return;
      case "available-no-access":
        // No-op; the tooltip already explains why this card is disabled.
        return;
      case "accessible":
        // Fire-and-forget: the SSO variant is async but the caller does not
        // need to wait — control transfers to the product (or to the
        // fallback) via window.location. Promise.resolve normalises the
        // union return type (void | Promise<void>) without raising no-void.
        Promise.resolve(openProduct(mod, instance)).catch(() => { /* strategy already falls back */ });
        break;
      default:
        // Exhaustive — TypeScript narrows mod.state to never here.
    }
  };

  const closeMenu = () => {
    // Only flip the open flag; the anchor + payload are cleared later, in the
    // Menu's onExited, so the items stay rendered through the fade-out (avoids
    // the blank white box that flashed when they unmounted mid-transition).
    setMenuOpen(false);
  };

  // Opens the grouped-card menu anchored to the activated card. The license
  // gate matches handleCardClick so an expired tenant can't open the menu.
  const openGroupMenu = (target: HTMLElement, mod: Module) => {
    if (license.status === "expired") return;

    setMenuAnchor(target);
    setMenuModule(mod);
    setMenuOpen(true);
  };

  // A child menu item runs the exact same 3-state logic as a normal card, then
  // closes the menu.
  const handleChildClick = (child: Module) => {
    handleCardClick(child);
    closeMenu();
  };

  // Card activation entry point: grouped cards open their menu, leaf cards open
  // the product directly. Shared by both click and keyboard handlers.
  const handleCardActivate = (target: HTMLElement, mod: Module) => {
    if (mod.children && mod.children.length > 0) {
      openGroupMenu(target, mod);

      return;
    }

    handleCardClick(mod);
  };

  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLElement>, mod: Module) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardActivate(e.currentTarget, mod);
    }
  };

  return (
    <Styled.LauncherWrap>
      <Styled.LauncherDeco>
        <Styled.DecoNavy />
        <Styled.DecoStripeW />
        <Styled.DecoStripeG />
      </Styled.LauncherDeco>
      <Styled.LauncherScroll>
        <Styled.LauncherInner>
          {license.status === "warning" && (
            <Styled.LicenseBanner>
              <span aria-hidden="true">⚠️</span>
              <p>{t(LauncherMessages.launcherLicenseWarning, { count: license.daysRemaining })}</p>
            </Styled.LicenseBanner>
          )}
          <AnnouncementBanner />
          <Styled.LauncherHead>
            <div>
              <Styled.Eyebrow>{t(LauncherMessages.launcherSelectModule)}</Styled.Eyebrow>
              <Styled.Greeting>{t(LauncherMessages.launcherGreeting, { name: userName })}</Styled.Greeting>
            </div>
            <Styled.ActivePill>
              <Styled.ActiveDot />
              {t(LauncherMessages.launcherActiveCount, { count: activeCount })}
            </Styled.ActivePill>
          </Styled.LauncherHead>

          {loading && !error && (
            <Styled.ModuleGrid role="status" aria-busy="true" aria-label={t(LauncherMessages.launcherLoading)}>
              {SKELETON_SLOTS.map((slot) => (
                <Styled.SkeletonCard key={slot} aria-hidden="true" />
              ))}
            </Styled.ModuleGrid>
          )}

          {error && <Styled.StateMessage>{t(LauncherMessages.launcherLoadError)}</Styled.StateMessage>}

          {!loading && !error && (
            <Styled.ModuleGrid>
              {groupedModules.map((mod, index) => {
                const isGroup = Boolean(mod.children && mod.children.length > 0);
                const desc = t(mod.description);
                // A group card never shows the "no access" hint on the card
                // itself — the per-app access is spelled out in its menu.
                const tooltipText = !isGroup && mod.state === "available-no-access"
                  ? `${desc} — ${t(LauncherMessages.launcherNoAccessTooltip)}`
                  : desc;

                return (
                  <Tooltip key={mod.id} title={tooltipText} arrow placement="top" enterDelay={250}>
                    <Styled.MCard
                      $state={mod.state}
                      $accent={mod.accent}
                      $index={index}
                      onClick={(e) => handleCardActivate(e.currentTarget, mod)}
                      onKeyDown={(e) => handleCardKeyDown(e, mod)}
                      role="button"
                      tabIndex={0}
                      aria-disabled={!isGroup && mod.state === "available-no-access"}
                      aria-haspopup={isGroup ? "menu" : undefined}
                      aria-expanded={isGroup ? (menuOpen && menuModule?.id === mod.id) : undefined}
                      aria-label={`${mod.label} — ${desc}`}
                    >
                      <Styled.MCardShine />
                      <ModuleHex accent={mod.accent} size={58} />
                      <Styled.MLabel $locked={mod.state !== "accessible"}>{mod.label}</Styled.MLabel>
                      {!isGroup && mod.state === "locked-not-contracted" && (
                        <Styled.LockBadge aria-label={t(LauncherMessages.launcherContactUs)}>
                          <span aria-hidden="true">🔒</span>
                        </Styled.LockBadge>
                      )}
                    </Styled.MCard>
                  </Tooltip>
                );
              })}
            </Styled.ModuleGrid>
          )}
        </Styled.LauncherInner>
      </Styled.LauncherScroll>
      {license.status === "expired" && (
        <Styled.LicenseBlock role="alert">
          <Styled.LicenseBlockCard>
            <span className="license-block-icon" aria-hidden="true">🔒</span>
            <h2>{t(LauncherMessages.launcherLicenseExpiredTitle)}</h2>
            <p>{t(LauncherMessages.launcherLicenseExpiredText)}</p>
          </Styled.LicenseBlockCard>
        </Styled.LicenseBlock>
      )}
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        // Clear the anchor + payload only AFTER the close animation finishes, so
        // the items stay mounted through the fade-out instead of blanking to an
        // empty white box mid-transition.
        TransitionProps={{ onExited: () => { setMenuAnchor(null); setMenuModule(null); } }}
        MenuListProps={{ "aria-label": menuModule ? menuModule.label : undefined }}
      >
        {menuModule?.children?.map((child) => {
          const childDesc = t(child.description);

          return (
            <MenuItem
              key={child.id}
              // available-no-access is disabled (no-op), matching the leaf-card
              // rule; locked-not-contracted stays enabled so it can open upsell.
              disabled={child.state === "available-no-access"}
              onClick={() => handleChildClick(child)}
            >
              <Styled.GroupMenuItem>
                <Styled.GroupMenuItemHead>
                  <Styled.GroupMenuItemLabel>{child.label}</Styled.GroupMenuItemLabel>
                  {child.state === "locked-not-contracted" && (
                    <Styled.GroupMenuItemLock aria-label={t(LauncherMessages.launcherContactUs)}>
                      <span aria-hidden="true">🔒</span>
                    </Styled.GroupMenuItemLock>
                  )}
                </Styled.GroupMenuItemHead>
                <Styled.GroupMenuItemDesc>{childDesc}</Styled.GroupMenuItemDesc>
              </Styled.GroupMenuItem>
            </MenuItem>
          );
        })}
      </Menu>
      <ModuleUpsellModal module={upsellModule} onClose={() => setUpsellModule(null)} />
    </Styled.LauncherWrap>
  );
};

export default Launcher;
